import { useState, useEffect } from 'preact/hooks';
import { store } from '../store';
import { leaveCheckoutHostPage, navigateToView } from '../navigation';
import { openContextualLogin } from '../returnTo';
import { getConfig } from '../config';
import { getOrder } from '../api';
import { t } from '../i18n';
import { isInlineViewOnCurrentPage } from '../inlinePages';

function SuccessIcon() {
  return (
    <svg width="72" height="72" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ color: '#198754' }}>
      <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.6" />
      <path d="M8 12.5l2.5 2.5L16 9.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
  );
}

function CancelIcon() {
  return (
    <svg width="72" height="72" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ color: '#6c757d' }}>
      <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.6" />
      <path d="M14.5 9.5l-5 5M9.5 9.5l5 5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
    </svg>
  );
}

function FailureIcon() {
  return (
    <svg width="72" height="72" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ color: '#dc3545' }}>
      <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.6" />
      <path d="M9 9l6 6M15 9l-6 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
    </svg>
  );
}

function pickPaymentLink(order: Record<string, unknown> | null): string {
  if (!order) return '';
  const link = order.payment_link ?? order.paymentLink;
  return typeof link === 'string' ? link.trim() : '';
}

export function OrderReturnPage() {
  const sorderid = store.getLastOrderId() ?? '';
  const intent = store.getOrderReturnIntent() ?? 'success';
  const isAuthed = !!store.getAuth();
  const [paymentLink, setPaymentLink] = useState('');
  const [loadingLink, setLoadingLink] = useState(false);

  useEffect(() => {
    if (!sorderid || intent === 'success') {
      setPaymentLink('');
      return;
    }
    setLoadingLink(true);
    const config = getConfig();
    getOrder(config, sorderid).then((res) => {
      setLoadingLink(false);
      if (res.status === 200 && res.result && typeof res.result === 'object') {
        setPaymentLink(pickPaymentLink(res.result as Record<string, unknown>));
      }
    });
  }, [sorderid, intent]);

  const inline = isInlineViewOnCurrentPage('order-return');
  const head = intent === 'success'
    ? { Icon: SuccessIcon, title: t('orderReturn.successTitle'), lead: t('orderReturn.successLead'), hint: t('orderReturn.successHint') }
    : intent === 'cancel'
      ? { Icon: CancelIcon, title: t('orderReturn.cancelTitle'), lead: t('orderReturn.cancelLead'), hint: t('orderReturn.cancelHint') }
      : { Icon: FailureIcon, title: t('orderReturn.failureTitle'), lead: t('orderReturn.failureLead'), hint: t('orderReturn.failureHint') };

  const openOrderDetails = () => {
    if (!sorderid) return;
    store.setOrdersFocusOrderId(sorderid);
    if (isAuthed) {
      navigateToView('orders');
      return;
    }
    openContextualLogin('orders');
  };

  const tryAgain = () => {
    if (paymentLink) {
      window.location.href = paymentLink;
      return;
    }
    openOrderDetails();
  };

  const goContactSupport = () => {
    const { viewUrls } = getConfig();
    const url = viewUrls?.contactus || viewUrls?.contact || '/contactus';
    if (typeof window !== 'undefined') {
      window.location.href = url.startsWith('http') ? url : new URL(url, window.location.origin).href;
    }
  };

  return (
    <div class={`shop-widget-order-return py-4 text-center${inline ? ' card shadow-sm border-0 px-3 px-md-4 mx-auto' : ''}`} style={inline ? { maxWidth: '560px' } : undefined}>
      <div class="mb-3"><head.Icon /></div>
      <h4 class="fw-bold mt-2 mb-1">{head.title}</h4>
      <p class="lead mb-2">{head.lead}</p>
      {head.hint && <p class="text-muted small mb-0">{head.hint}</p>}

      {sorderid && (
        <div class="mt-4 mb-2">
          <span class="text-muted small me-2">{t('orderSuccess.orderId')}</span>
          <strong class="fs-6">#{sorderid}</strong>
        </div>
      )}

      <div class="d-flex justify-content-center gap-2 flex-wrap mt-4">
        {sorderid && (
          <button type="button" class="btn btn-primary" onClick={openOrderDetails}>
            {isAuthed ? t('orderReturn.btnDetails') : t('orderReturn.btnSignIn')}
          </button>
        )}
        {intent === 'success' && (
          <button type="button" class="btn btn-outline-secondary" onClick={() => leaveCheckoutHostPage()}>
            {t('orderReturn.btnContinueShopping')}
          </button>
        )}
        {(intent === 'cancel' || intent === 'failure') && (
          <>
            <button
              type="button"
              class="btn btn-outline-primary"
              onClick={tryAgain}
              disabled={loadingLink}
            >
              {loadingLink ? t('orders.detailsLoading') : t('orderReturn.btnTryAgain')}
            </button>
            {intent === 'cancel' && (
              <button type="button" class="btn btn-link" onClick={() => leaveCheckoutHostPage()}>
                {t('orderReturn.btnContinueShopping')}
              </button>
            )}
            {intent === 'failure' && (
              <button type="button" class="btn btn-link" onClick={goContactSupport}>
                {t('orderReturn.btnContactSupport')}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

